import { PresentationType, MonetizationType } from '@/@types/graphql-types';
import { TitleObjectType } from '@/interfaces/titles';

// import { getVm } from '@/helpers/vm-helper';
// const vm = getVm();

// UTIL
type valueof<T> = T[keyof T];

interface SponsoredRecommendationData {
	id: string;
	name: string;
	countries: Array<string>;
	start_date: number;
	end_date: number;
	provider_id: number;
	genres: string[];
	type: SponsoredRecommendationType;
	promotion: SponsoredRecommendationPromotion;
	content: {
		id: string;
		objectType: TitleObjectType;
		objectId: number;
		watchNowOffer: {
			[key: string]: {
				/** @Warning Do not use a link that starts with click.justwatch.com or it will get double affiliated! */
				standardWebURL: string;
				currency: string;
				package: {
					clearName: string;
					packageId: number;
				};
				monetizationType: MonetizationType;
				presentationType: PresentationType;
			};
		};
	};
	overrideBuyboxClickout: boolean;
	hideRating: boolean;
	watchnowLabel?: string;
	/** Enter the backdrop image name WITHOUT its format type (e.g: without .png) */
	backdropImageName: string;
	impressionTagInternal: string;
	impressionTagExternal: string;
}

type SponsoredRecommendationType = valueof<typeof SponsoredRecommendationType>;
const SponsoredRecommendationType = {
	Regular: 'REGULAR',
	Cinematic: 'CINEMATIC',
} as const;

type SponsoredRecommendationPromotion = valueof<typeof SponsoredRecommendationPromotion>;
const SponsoredRecommendationPromotion = {
	/** Sponsored Recommendation in SimilarTitles at bottom of TitleDetail */
	None: 'NONE',

	/** Sponsored Recommendation in SimilarTitles below BuyBox */
	Low: 'LOW',

	/** Sponsored Recommendation in SimilarTitles above Buybox */
	High: 'HIGH',
} as const;

// List of current Sponsored Recommendations:
const sponsoredRecommendations: Array<SponsoredRecommendationData> = [
	{
		id: 'cceff288-58c7-49dc-9468-79227e9ca585',
		name: 'The Idol',
		countries: ['DE'],
		start_date: new Date('2023-06-02').setUTCHours(0, 0, 0, 0),
		end_date: new Date('2023-06-07').setUTCHours(23, 59, 59, 999),
		provider_id: 30, // WOW
		genres: ['drm'], // Drama
		type: SponsoredRecommendationType.Regular,
		promotion: SponsoredRecommendationPromotion.None,
		content: {
			id: 'ts312190', // The Idol
			objectType: TitleObjectType.SHOW,
			objectId: 312190, // The Idol
			watchNowOffer: {
				DE: {
					standardWebURL:
						'https://ad.doubleclick.net/ddm/trackclk/N1111979.2621703JUSTWATCHGMBH/B29969369.367349876;dc_trk_aid=558901019;dc_trk_cid=193068516;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
					currency: 'EUR',
					package: {
						clearName: 'WOW',
						packageId: 30, // WOW
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
			},
		},
		overrideBuyboxClickout: true,
		hideRating: false, // Hides IMDb rating on SR poster if true
		watchnowLabel: undefined,
		backdropImageName: 'wow-the_idol-1', // Enter the backdrop image name WITHOUT its format type (e.g: without .png)
		impressionTagInternal:
			'https://ad.doubleclick.net/ddm/ad/N299201.289800DQNA0/B29974531.368616957;sz=1x1;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;dc_sdk_apis=[APIFRAMEWORKS];dc_omid_p=[OMIDPARTNER];gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};dc_tdv=1?',
		impressionTagExternal:
			'https://ad.doubleclick.net/ddm/trackimp/N1111979.2621703JUSTWATCHGMBH/B29969369.367349876;dc_trk_aid=558901019;dc_trk_cid=193068516;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=;dc_tdv=1?',
	},
	{
		id: '84a2dbc2-7a18-46d2-824c-14a28f5f8591',
		name: 'Avatar: The Way of Water',
		countries: ['BR'],
		start_date: new Date('2023-06-07').setUTCHours(0, 0, 0, 0),
		end_date: new Date('2023-06-11').setUTCHours(23, 59, 59, 999),
		provider_id: 337, // Disney+
		genres: ['act', 'scf'], // Action & Adventure, Science-Fiction
		type: SponsoredRecommendationType.Regular,
		promotion: SponsoredRecommendationPromotion.Low,
		content: {
			id: 'tm1904', // Avatar: The Way of Water
			objectType: TitleObjectType.MOVIE,
			objectId: 1904, // Avatar: The Way of Water
			watchNowOffer: {
				BR: {
					standardWebURL:
						'https://disneyplus.bn5x.net/c/1206980/1712067/9358?sharedid=sponsoredrecommendation',
					currency: 'BRL',
					package: {
						clearName: 'Disney Plus',
						packageId: 337, // Disney+
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
			},
		},
		overrideBuyboxClickout: true,
		hideRating: false, // Hides IMDb rating on SR poster if true
		watchnowLabel: undefined,
		backdropImageName: 'disney-avatar_the_way_of_water-1', // Enter the backdrop image name WITHOUT its format type (e.g: without .png)
		impressionTagInternal: '',
		impressionTagExternal: '',
	},
	{
		id: '98fbcab8-0d1d-406b-95c0-ca8d1e72ff29',
		name: 'Kohlrabenschwarz',
		countries: ['DE'],
		start_date: new Date('2023-06-08').setUTCHours(0, 0, 0, 0),
		end_date: new Date('2023-06-11').setUTCHours(23, 59, 59, 999),
		provider_id: 531, // Paramount+
		genres: ['cmy', 'crm', 'trl'], // Comedy, Crime, Mystery & Thriller
		type: SponsoredRecommendationType.Regular,
		promotion: SponsoredRecommendationPromotion.None,
		content: {
			id: 'ts367675', // Kohlrabenschwarz
			objectType: TitleObjectType.SHOW,
			objectId: 367675, // Kohlrabenschwarz
			watchNowOffer: {
				DE: {
					standardWebURL: 'https://www.paramountplus.com/?ftag=IPP-10-10aie0i',
					currency: 'EUR',
					package: {
						clearName: 'Paramount Plus',
						packageId: 531, // Paramount+
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
			},
		},
		overrideBuyboxClickout: true,
		hideRating: false, // Hides IMDb rating on SR poster if true
		watchnowLabel: undefined,
		backdropImageName: 'paramount-kohlrabenschwarz-1', // Enter the backdrop image name WITHOUT its format type (e.g: without .png)
		impressionTagInternal: '',
		impressionTagExternal: '',
	},
];

const customSponsoredRecommendations: Array<any> = [
	// {
	// 	id: 'a41049ba-0775-4a43-8923-9af4f97df32d',
	// 	name: 'JustWatch Favorites',
	// 	countries: [
	// 		'US',
	// 		'UK',
	// 		'AU',
	// 		'NZ',
	// 		'IE',
	// 		'IN',
	// 		'ZA',
	// 		'NG',
	// 		'CA',
	// 		'KE',
	// 		'AE',
	// 		'SA',
	// 		'BA',
	// 		'QA',
	// 		'EG',
	// 		'PL',
	// 		'NL',
	// 		'BE',
	// 		'IT',
	// 		'FR',
	// 		'CH',
	// 		'DZ',
	// 		'ES',
	// 		'MX',
	// 		'AR',
	// 		'DE',
	// 		'AT',
	// 		'PT',
	// 		'BR',
	// 		'TR',
	// 	],
	// 	start_date: new Date('2023-04-07').setUTCHours(0, 0, 0, 0),
	// 	end_date: new Date('2023-04-10').setUTCHours(23, 59, 59, 999),
	// 	title: 'JustWatch Favorites',
	// 	text: vm.$tc('WEBAPP_LIST_JWFAVORITES_CONTEXT'),
	// 	fullPath: 'https://justwatch.com/shared?id=tl-tu-4ab579f5-b3e7-4fb5-8c9d-1032cd7befd3',
	// 	backdropImageName: 'jw_favorites-2',
	// },
];

const getSponsoredRecommendations = (country: string) => {
	const now = new Date().getTime();
	return sponsoredRecommendations.filter((sr: SponsoredRecommendationData) => {
		return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
	});
};

const getCustomSponsoredRecommendations = (country: string) => {
	const now = new Date().getTime();
	return customSponsoredRecommendations.filter((sr: any) => {
		return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
	});
};

const sponsoredRecommendationProviders = () => {
	return sponsoredRecommendations.map((sr: SponsoredRecommendationData) => {
		return sr.provider_id;
	});
};

const isSponsoredRecommendationTitle = (id: string | undefined, country: string) => {
	const now = new Date().getTime();
	return !!sponsoredRecommendations.find((sr: SponsoredRecommendationData) => {
		return sr.content.id === id && sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
	});
};

const showSponsoredRecommendations = (country: string) => {
	const now = new Date().getTime();
	return (
		!!sponsoredRecommendations.find((sr: SponsoredRecommendationData) => {
			return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
		}) ||
		!!customSponsoredRecommendations.find((sr: any) => {
			return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
		})
	);
};

export {
	SponsoredRecommendationData,
	sponsoredRecommendations,
	customSponsoredRecommendations,
	getSponsoredRecommendations,
	getCustomSponsoredRecommendations,
	sponsoredRecommendationProviders,
	isSponsoredRecommendationTitle,
	showSponsoredRecommendations,
	SponsoredRecommendationPromotion,
	SponsoredRecommendationType,
};
